// MarcusAI Workbench (Capability 3, MW-4) — the fix workspace. One page wired to
// the MW-1 read, MW-2 task writes, and MW-3 Marcus draft stream. Reached from the
// dashboard "Improve AI Trust Signals" card and the sidebar "Workbench" entry; it
// replaces the retired linear Recommendations flow.
//
// Company-scoped: the route carries the company id as `:projectId` (same as the
// dashboard / Sources), and MW-1 resolves that company's latest paid report. Fix
// state is bound to company+signal, so ticks/notes/done survive the monthly
// re-scan. Paid-only: free users are bounced to billing (the endpoint 403s).

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { CheckCircle2, Wrench } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { VbPageHeader } from "@/components/vb/VbPageHeader";
import { VbError, VbLoading } from "@/components/vb/states";
import { Segmented, Callout } from "@/components/vb";
import { useWorkbenchQueue, useUpdateTask } from "@/hooks/workbench/useWorkbench";
import { trackWorkbenchViewed } from "@/lib/posthog";
import type { WorkbenchTierProgress } from "@/types/workbench";
import { ProgressStrip } from "@/components/workbench/ProgressStrip";
import { FixQueue } from "@/components/workbench/FixQueue";
import { ActiveFix } from "@/components/workbench/ActiveFix";
import { groupFixes, nextOpenKey, fixLifecycle } from "./workbenchUtil";

const TIER_ORDER = ["technical", "authority", "brand"];

export function WorkbenchPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const companyId = projectId && /^\d+$/.test(projectId) ? Number(projectId) : undefined;
  const { token } = useAuth();
  const { activeWorkspace } = useWorkspace();

  const { data, isLoading, isError, error, refetch } = useWorkbenchQueue(companyId, true);
  const update = useUpdateTask(companyId ?? -1);
  const busy = update.isPending;

  const fixes = useMemo(() => data?.fixes ?? [], [data]);
  // The report the queue is built from — used to tell a genuinely-completed fix
  // (awaiting the next scan) from one the latest report *re-flagged* after it was
  // marked done (regressed). See fixLifecycle.
  const reportDate = data?.report?.created_at ?? null;
  const [groupBy, setGroupBy] = useState<"severity" | "tier">("severity");
  const [selectedKey, setSelectedKey] = useState<number | null>(null);

  // Mobile is single-view: the queue OR the active fix, never both (desktop keeps
  // the split panel — this flag is inert there, gated off by the container query).
  // "list" is the default; arriving with an `openTopFix` intent (dashboard "Start
  // now" / a signal drawer's "Start in Workbench") jumps straight to the detail so
  // the user isn't dropped on a list they then have to scroll and tap. Read once
  // on mount; the queue-row tap and the detail's back control drive it after that.
  const [mobileView, setMobileView] = useState<"list" | "fix">(() =>
    (location.state as { openTopFix?: boolean } | null)?.openTopFix ? "fix" : "list",
  );

  const openFix = (signalKey: number) => {
    setSelectedKey(signalKey);
    setMobileView("fix");
  };

  // Reset the window scroll to the top of the detail whenever it opens (or
  // auto-advances) in the mobile single-view. Without this, tapping a row low in
  // a long queue leaves the scroll where it was; the shorter detail then clamps
  // it to the bottom, stranding the user on the footer with the title + back
  // control above the fold — the very "nothing happened" symptom this fixes
  // (AITS-602 #2). Gated on the queue actually being hidden, which is true only
  // in the mobile single-view, so desktop's sticky split panel is never yanked.
  // useLayoutEffect so the scroll lands before paint (no flash of the footer).
  const pageRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (mobileView !== "fix") return;
    const queue = pageRef.current?.querySelector(".wb-queue");
    if (queue && getComputedStyle(queue).display === "none") {
      window.scrollTo(0, 0);
    }
  }, [mobileView, selectedKey]);

  // Keep a valid selection: first outstanding fix on load, or after the selected
  // fix disappears (workspace switch); fall back to the first fix if all are done.
  useEffect(() => {
    if (!fixes.length) return;
    const exists = selectedKey != null && fixes.some((f) => f.signal_key === selectedKey);
    if (!exists) {
      const firstOpen = fixes.find((f) => fixLifecycle(f, reportDate) !== "done");
      setSelectedKey((firstOpen ?? fixes[0]).signal_key);
    }
  }, [fixes, selectedKey, reportDate]);

  // Fire `workbench_viewed` once per company view, when the queue first loads.
  // Ref-gated (not a session-wide set) so switching company re-emits but a
  // refetch / re-render / StrictMode double-invoke of the same company does not —
  // the same "control frequency from the page" shape the other *_viewed events
  // use. Counts read the backend progress rollup (task-status based), so they
  // line up with the fix_marked_done event's `fixes_remaining`.
  const viewedForCompany = useRef<number | null>(null);
  useEffect(() => {
    if (companyId == null || !data) return;
    if (viewedForCompany.current === companyId) return;
    viewedForCompany.current = companyId;
    trackWorkbenchViewed({
      companyId,
      companyName: data.company?.name ?? null,
      fixesRemaining: data.progress.remaining,
      fixesDone: data.progress.done,
    });
  }, [companyId, data]);

  const selectedFix = useMemo(
    () => fixes.find((f) => f.signal_key === selectedKey) ?? null,
    [fixes, selectedKey],
  );

  const groups = useMemo(
    () => groupFixes(fixes, groupBy, reportDate),
    [fixes, groupBy, reportDate],
  );

  // Progress + per-tier counts derived from `fixes` so the ring/pills react to a
  // mark-done without waiting on a refetch. "Done" here means genuinely done
  // (not a regressed fix the latest report re-flagged) — the ring must not count
  // still-failing work as complete.
  const doneCount = useMemo(
    () => fixes.filter((f) => fixLifecycle(f, reportDate) === "done").length,
    [fixes, reportDate],
  );
  const byTier = useMemo<WorkbenchTierProgress[]>(() => {
    const map = new Map<string, WorkbenchTierProgress>();
    fixes.forEach((f) => {
      const e = map.get(f.tier) ?? { tier: f.tier, label: f.tier_label, total: 0, done: 0 };
      e.total += 1;
      if (fixLifecycle(f, reportDate) === "done") e.done += 1;
      map.set(f.tier, e);
    });
    return TIER_ORDER.filter((t) => map.has(t)).map((t) => map.get(t)!);
  }, [fixes, reportDate]);

  // ── Task mutations (bound to the selected fix in the render) ──
  const workspaceId = activeWorkspace?.id ?? null;

  const markDone = (signalKey: number) => {
    const advanceTo = nextOpenKey(fixes, signalKey, reportDate);
    update.mutate(
      { signalKey, patch: { status: "done" } },
      {
        onSuccess: () => {
          toast.success("Fix marked done", {
            description: "We'll re-check it automatically on your next monthly report.",
          });
          if (advanceTo != null) setSelectedKey(advanceTo);
        },
        onError: () => toast.error("Couldn't update the fix — try again."),
      },
    );
  };

  if (!companyId) return null;

  return (
    // Only enter the full-screen detail when there's actually a fix to show, so a
    // stray "fix" state can never strand the user on the back-less empty pane.
    <div
      ref={pageRef}
      className="vb-root wb-page"
      data-mobile-view={mobileView === "fix" && selectedFix ? "fix" : "list"}
    >
      <VbPageHeader
        title="Workbench"
        subtitle={
          data?.company?.name
            ? `Fix what AI gets wrong about ${data.company.name}`
            : "Fix what AI gets wrong about your business"
        }
        actions={
          fixes.length > 0 ? (
            <Segmented
              ariaLabel="Group fixes by"
              size="sm"
              value={groupBy}
              onChange={setGroupBy}
              options={[
                { value: "severity", label: "By priority" },
                { value: "tier", label: "By tier" },
              ]}
            />
          ) : undefined
        }
      />

      {isLoading ? (
        <VbLoading rows={4} />
      ) : isError ? (
        renderError(error, refetch)
      ) : fixes.length === 0 ? (
        <Callout tone="success" icon={<CheckCircle2 size={20} />}>
          You're all clear — no open AI Trust Signal fixes on your latest report.
        </Callout>
      ) : (
        <>
          <ProgressStrip
            done={doneCount}
            total={fixes.length}
            byTier={byTier}
            reportDate={data?.report?.created_at}
          />
          <div className="wb-layout">
            <FixQueue
              groups={groups}
              totalCount={fixes.length}
              activeKey={selectedKey}
              reportDate={reportDate}
              onSelect={openFix}
            />
            {selectedFix ? (
              <ActiveFix
                // Remount per fix so transient state (streamed drafts, the
                // evidence "show all" toggle, the notes draft) resets cleanly
                // instead of leaking across a selection change.
                key={selectedFix.signal_key}
                fix={selectedFix}
                companyId={companyId}
                token={token}
                workspaceId={workspaceId}
                reportDate={reportDate}
                busy={busy}
                onBack={() => setMobileView("list")}
                onToggleStep={(idx, done) =>
                  update.mutate({
                    signalKey: selectedFix.signal_key,
                    patch: { step_state: { [String(idx)]: done } },
                  })
                }
                onMarkDone={() => markDone(selectedFix.signal_key)}
                onReopen={() =>
                  update.mutate({ signalKey: selectedFix.signal_key, patch: { status: "open" } })
                }
                onSaveNotes={(notes) =>
                  update.mutate(
                    { signalKey: selectedFix.signal_key, patch: { notes } },
                    { onSuccess: () => toast.success("Notes saved") },
                  )
                }
              />
            ) : (
              <div className="wb-empty-pane">
                <Wrench size={22} />
                <span>Select a fix from the queue to get started.</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** A 404 means the company has no accessible completed paid report to work from;
 *  anything else is a genuine load error with a retry. */
function renderError(error: unknown, onRetry: () => void) {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 404) {
    return (
      <VbError
        title="No report to work from yet"
        description="Run a paid AI Trust Signal report for this company, then come back to fix what AI gets wrong."
      />
    );
  }
  return (
    <VbError
      title="We couldn't load your workbench"
      description="Something went wrong fetching your fixes."
      onRetry={onRetry}
    />
  );
}

export default WorkbenchPage;
